import React, { useState, useRef } from "react";
import { X } from "lucide-react";

/**
 * Tiny tags input. Free-form text; Enter/comma commits a tag. Backspace on an
 * empty input removes the last tag. No suggestions list — users own their tag
 * vocabulary (per Q2 spec).
 */
export function TagsInput({ value = [], onChange, placeholder = "Add tag…", testId, disabled }) {
  const [text, setText] = useState("");
  const inputRef = useRef(null);

  const commit = (raw) => {
    const t = (raw || "").trim().replace(/,/g, "").toLowerCase();
    if (!t) return;
    if (value.includes(t)) { setText(""); return; }
    onChange([...value, t]);
    setText("");
  };

  const remove = (idx) => {
    onChange(value.filter((_, i) => i !== idx));
  };

  const onKey = (e) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      commit(text);
    } else if (e.key === "Backspace" && !text && value.length > 0) {
      e.preventDefault();
      remove(value.length - 1);
    }
  };

  return (
    <div
      className={`flex flex-wrap items-center gap-1 px-2 py-1.5 rounded border border-border bg-background ${disabled ? "opacity-60" : ""}`}
      onClick={() => inputRef.current?.focus()}
      data-testid={testId}
    >
      {value.map((tag, i) => (
        <span
          key={tag}
          className="inline-flex items-center gap-1 text-xs px-1.5 py-0.5 rounded bg-primary/15 text-primary border border-primary/30"
          data-testid={`tag-chip-${tag}`}
        >
          {tag}
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); remove(i); }}
            className="hover:text-rose-300"
            disabled={disabled}
            aria-label={`Remove ${tag}`}
            data-testid={`tag-chip-${tag}-remove`}
          >
            <X className="w-3 h-3" />
          </button>
        </span>
      ))}
      <input
        ref={inputRef}
        value={text}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={onKey}
        onBlur={() => commit(text)}
        disabled={disabled}
        placeholder={value.length === 0 ? placeholder : ""}
        className="flex-1 min-w-[80px] bg-transparent text-sm outline-none placeholder:text-muted-foreground"
        data-testid={testId ? `${testId}-input` : undefined}
      />
    </div>
  );
}
